(function () {
  'use strict';

  if (customElements.get('see-in-action-xl-v2')) return;

  class SeeInActionXLV2 extends HTMLElement {
    constructor() {
      super();
      this.initialized = false;
      this.rafId = null;
      this.childObserver = null;
      this.resizeObserver = null;
      this.popupAnimation = null;
      this.popupClosing = false;
      this.onPrevious = this.onPrevious.bind(this);
      this.onNext = this.onNext.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onImageClick = this.onImageClick.bind(this);
      this.onPopupKeydown = this.onPopupKeydown.bind(this);
      this.onPopupClick = this.onPopupClick.bind(this);
      this.onPopupClose = this.onPopupClose.bind(this);
      this.closePopup = this.closePopup.bind(this);
      this.scheduleUpdate = this.scheduleUpdate.bind(this);
      this.updateControls = this.updateControls.bind(this);
    }

    connectedCallback() {
      if (this.initialized) return;

      if (this.initialize()) return;

      if ('MutationObserver' in window && !this.childObserver) {
        this.childObserver = new MutationObserver(() => this.initialize());
        this.childObserver.observe(this, { childList: true, subtree: true });
      }
    }

    initialize() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return false;

      if (this.childObserver) {
        this.childObserver.disconnect();
        this.childObserver = null;
      }

      this.previousButton = this.querySelector('[data-previous]');
      this.nextButton = this.querySelector('[data-next]');
      this.controls = this.querySelector('[data-controls]');
      this.popup = this.querySelector('[data-popup]');
      this.popupImage = this.querySelector('[data-popup-image]');
      this.popupStatus = this.querySelector('[data-popup-status]');
      this.popupStatusText = this.popupStatus.querySelector('span');
      this.popupCloseButton = this.querySelector('[data-popup-close]');

      if (this.previousButton) this.previousButton.addEventListener('click', this.onPrevious);
      if (this.nextButton) this.nextButton.addEventListener('click', this.onNext);
      this.track.addEventListener('keydown', this.onKeydown);
      this.track.addEventListener('click', this.onImageClick);
      if (this.popup) {
        this.popup.addEventListener('keydown', this.onPopupKeydown);
        this.popup.addEventListener('click', this.onPopupClick);
        this.popup.addEventListener('cancel', this.closePopup);
        this.popup.addEventListener('close', this.onPopupClose);
      }
      if (this.popupCloseButton) this.popupCloseButton.addEventListener('click', this.closePopup);
      this.track.addEventListener('scroll', this.scheduleUpdate, { passive: true });
      window.addEventListener('resize', this.scheduleUpdate);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.scheduleUpdate);
        this.resizeObserver.observe(this.track);
      }

      this.initialized = true;
      this.classList.add('is-ready');
      this.updateControls();
      return true;
    }

    disconnectedCallback() {
      if (this.childObserver) {
        this.childObserver.disconnect();
        this.childObserver = null;
      }

      if (!this.initialized) return;

      if (this.previousButton) this.previousButton.removeEventListener('click', this.onPrevious);
      if (this.nextButton) this.nextButton.removeEventListener('click', this.onNext);
      this.track.removeEventListener('keydown', this.onKeydown);
      this.track.removeEventListener('click', this.onImageClick);
      if (this.popup) {
        this.popup.removeEventListener('keydown', this.onPopupKeydown);
        this.popup.removeEventListener('click', this.onPopupClick);
        this.popup.removeEventListener('cancel', this.closePopup);
        this.popup.removeEventListener('close', this.onPopupClose);
        if (this.popupAnimation) this.popupAnimation.cancel();
        this.popupAnimation = null;
        this.popupClosing = false;
        if (this.popup.open) this.popup.close();
      }
      if (this.popupCloseButton) this.popupCloseButton.removeEventListener('click', this.closePopup);
      this.track.removeEventListener('scroll', this.scheduleUpdate);
      window.removeEventListener('resize', this.scheduleUpdate);

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }

      if (this.rafId !== null) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }

      this.initialized = false;
      this.classList.remove('is-ready');
    }

    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    scrollBehavior() {
      return this.prefersReducedMotion() ? 'auto' : 'smooth';
    }

    pageDistance() {
      const styles = window.getComputedStyle(this.track);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      return this.track.clientWidth + gap;
    }

    onImageClick(event) {
      const button = event.target.closest('[data-image]');
      if (!button || !this.popup || !this.popupImage) return;

      const image = button.querySelector('img');
      const source = image.getBoundingClientRect();
      this.popupSourceButton = button;
      this.popupSourceImage = image;
      this.popup.dataset.imageState = 'loading';
      this.popup.setAttribute('aria-busy', 'true');
      this.popupStatusText.textContent = this.popupStatus.dataset.loadingLabel;
      this.popupStatusText.classList.add('sr-only');
      this.popupStatus.hidden = false;
      const popupImage = this.popupImage.cloneNode(false);
      popupImage.removeAttribute('src');
      this.popupImage.replaceWith(popupImage);
      this.popupImage = popupImage;
      this.popupImage.style.setProperty('--tw-see-in-action-xl-v2-popup-ratio', source.width / source.height);
      this.popupImage.width = 2400;
      this.popupImage.height = Math.round(2400 * source.height / source.width);
      this.popupImage.alt = image.alt;
      const finishLoading = () => {
        if (this.popupImage !== popupImage) return;
        const loaded = popupImage.naturalWidth > 0;
        this.popup.dataset.imageState = loaded ? 'ready' : 'error';
        this.popup.setAttribute('aria-busy', 'false');
        this.popupStatus.hidden = loaded;
        if (!loaded) {
          this.popupStatusText.textContent = this.popupStatus.dataset.errorLabel;
          this.popupStatusText.classList.remove('sr-only');
        }
      };
      popupImage.addEventListener('load', finishLoading, { once: true });
      popupImage.addEventListener('error', finishLoading, { once: true });
      popupImage.src = button.dataset.fullImage;
      if (popupImage.complete) finishLoading();
      if (this.popupAnimation) this.popupAnimation.cancel();
      this.popupAnimation = null;
      this.popupClosing = false;
      this.popup.showModal();

      if (!this.prefersReducedMotion()) {
        const origin = image.getBoundingClientRect();
        const target = this.popup.getBoundingClientRect();
        this.popupAnimation = this.popup.animate([
          {
            transform: this.popupOriginTransform(origin, target),
            opacity: 0.3,
          },
          { transform: 'none', opacity: 1 },
        ], { duration: 240, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)', fill: 'both' });
      }
    }

    popupOriginTransform(source, target) {
      return `translate(${source.x - target.x}px, ${source.y - target.y}px) scale(${source.width / target.width}, ${source.height / target.height})`;
    }

    closePopup(event) {
      if (event) event.preventDefault();
      if (!this.popup.open || this.popupClosing) return;

      if (this.prefersReducedMotion() || !this.popupAnimation) {
        if (this.popupAnimation) this.popupAnimation.cancel();
        this.popupAnimation = null;
        this.popup.close();
        return;
      }

      this.popupClosing = true;
      const animation = this.popupAnimation;
      if (animation.playState === 'finished') {
        const frames = animation.effect.getKeyframes();
        frames[0].transform = this.popupOriginTransform(this.popupSourceImage.getBoundingClientRect(), this.popup.getBoundingClientRect());
        animation.effect.setKeyframes(frames);
      }
      const backdropAnimation = this.popup.getAnimations({ subtree: true }).find((candidate) => candidate.animationName === 'tw-see-in-action-xl-v2-backdrop-in');
      if (backdropAnimation) backdropAnimation.reverse();
      animation.onfinish = () => {
        this.popup.close();
        animation.cancel();
        this.popupAnimation = null;
        this.popupClosing = false;
      };
      animation.reverse();
    }

    onPopupClick(event) {
      if (event.target === this.popup) this.closePopup();
    }

    onPopupClose() {
      if (this.popupSourceButton && this.popupSourceButton.isConnected) {
        this.popupSourceButton.focus({ preventScroll: true });
      }
    }

    onPopupKeydown(event) {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      this.closePopup();
    }

    onPrevious() {
      this.track.scrollBy({ left: -this.pageDistance(), behavior: this.scrollBehavior() });
    }

    onNext() {
      this.track.scrollBy({ left: this.pageDistance(), behavior: this.scrollBehavior() });
    }

    onKeydown(event) {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

      event.preventDefault();
      const behavior = this.scrollBehavior();
      const focusedImage = event.target.closest('[data-image]');

      if (focusedImage) {
        const images = Array.from(this.track.querySelectorAll('[data-image]'));
        const index = images.indexOf(focusedImage);
        let nextIndex = index;
        if (event.key === 'ArrowLeft') nextIndex = Math.max(0, index - 1);
        else if (event.key === 'ArrowRight') nextIndex = Math.min(images.length - 1, index + 1);
        else if (event.key === 'Home') nextIndex = 0;
        else nextIndex = images.length - 1;
        images[nextIndex].focus({ preventScroll: true });
        images[nextIndex].scrollIntoView({ block: 'nearest', inline: 'start', behavior });
        return;
      }

      if (event.key === 'ArrowLeft') {
        this.track.scrollBy({ left: -this.pageDistance(), behavior });
      } else if (event.key === 'ArrowRight') {
        this.track.scrollBy({ left: this.pageDistance(), behavior });
      } else if (event.key === 'Home') {
        this.track.scrollTo({ left: 0, behavior });
      } else {
        this.track.scrollTo({ left: this.track.scrollWidth, behavior });
      }
    }

    scheduleUpdate() {
      if (this.rafId !== null) return;

      this.rafId = requestAnimationFrame(() => {
        this.rafId = null;
        this.updateControls();
      });
    }

    updateControls() {
      const tolerance = 1;
      const hasOverflow = this.track.scrollWidth - this.track.clientWidth > tolerance;
      const atStart = this.track.scrollLeft <= tolerance;
      const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - tolerance;

      if (this.controls) this.controls.hidden = !hasOverflow;
      if (this.previousButton) this.previousButton.disabled = !hasOverflow || atStart;
      if (this.nextButton) this.nextButton.disabled = !hasOverflow || atEnd;
    }
  }

  customElements.define('see-in-action-xl-v2', SeeInActionXLV2);
})();
